import React from "react";

import { ArrowRightIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { UnidentifiedPayment } from "~/pages/payments/hooks/useUnidentifiedPayments";

interface UnidentifiedPaymentsTableProps {
  payments: UnidentifiedPayment[];
  loading: boolean;
  onAssignClick: (paymentId: number) => void;
}

export default function UnidentifiedPaymentsTable({
  payments,
  loading,
  onAssignClick,
}: UnidentifiedPaymentsTableProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-CL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (loading) {
    return <TableSkeleton rows={5} columns={5} />;
  }

  if (payments.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">No hay pagos sin identificar.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fecha</TableHead>
          <TableHead>RUT</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead className="text-right">Monto</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {payments.map((payment, index) => (
          <TableRow
            key={payment.id}
            style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
          >
            <TableCell>{formatDate(payment.createdAt)}</TableCell>
            <TableCell>{payment.rut || "No disponible"}</TableCell>
            <TableCell>{payment.name || "No disponible"}</TableCell>
            <TableCell className="text-right font-semibold">
              {formatCurrency(payment.amount)}
            </TableCell>
            <TableCell>
              <div className="flex justify-end">
                <Button
                  onClick={() => onAssignClick(payment.id)}
                  className="flex items-center gap-2"
                >
                  Asignar
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
